
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  StopCircle,
  AlertTriangle 
} from 'lucide-react';
import { useEnrichmentQueue } from '@/hooks/useEnrichmentQueue';
import { Investor } from '@/types/investor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface EnrichmentQueueProps {
  investors: Investor[];
  relevanceContext: string;
  isInvestorProcessed: (investor: Investor) => boolean;
  onJobComplete?: () => void;
}

const EnrichmentQueue: React.FC<EnrichmentQueueProps> = ({
  investors,
  relevanceContext,
  isInvestorProcessed,
  onJobComplete
}) => {
  const [openaiKey, setOpenaiKey] = useState('');
  const [perplexityKey, setPerplexityKey] = useState('');
  const [keysStored, setKeysStored] = useState(false);

  const {
    currentJob,
    isSubmitting,
    isCancelling,
    storeApiKeys,
    submitEnrichmentJob,
    cancelEnrichmentJob,
    setupRealtimeMonitoring
  } = useEnrichmentQueue();

  // Load existing keys from localStorage on mount
  useEffect(() => {
    const storedOpenaiKey = localStorage.getItem('openai_token') || '';
    const storedPerplexityKey = localStorage.getItem('perplexity_token') || '';
    
    setOpenaiKey(storedOpenaiKey);
    setPerplexityKey(storedPerplexityKey);
    
    // If keys exist, mark as stored
    if (storedOpenaiKey || storedPerplexityKey) {
      setKeysStored(true);
    }
  }, []);

  // Monitor job completion
  useEffect(() => {
    if (currentJob?.status === 'completed' && onJobComplete) {
      onJobComplete();
    }
  }, [currentJob?.status, onJobComplete]);

  const handleStoreKeys = async () => {
    if (!openaiKey && !perplexityKey) {
      return;
    }

    const success = await storeApiKeys(openaiKey || undefined, perplexityKey || undefined);
    if (success) {
      setKeysStored(true);
      // Also store in localStorage for backward compatibility
      if (openaiKey) localStorage.setItem('openai_token', openaiKey);
      if (perplexityKey) localStorage.setItem('perplexity_token', perplexityKey);
    }
  };

  const handleStartEnrichment = async () => {
    if (!relevanceContext) {
      return;
    }

    // Get unprocessed investors
    const unprocessedInvestors = investors.filter(inv => !isInvestorProcessed(inv));
    
    if (unprocessedInvestors.length === 0) {
      return;
    }

    const investorIds = unprocessedInvestors.map(inv => inv.id);
    const jobId = await submitEnrichmentJob(investorIds, relevanceContext);
    
    if (jobId) {
      setupRealtimeMonitoring(jobId);
    }
  };

  const handleCancelJob = async () => {
    if (!currentJob) return;
    
    const success = await cancelEnrichmentJob(currentJob.jobId, 'User cancelled via UI');
    if (success) {
      // The real-time subscription will update the job status
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <StopCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const unprocessedCount = investors.filter(inv => !isInvestorProcessed(inv)).length;
  const totalCount = investors.length;
  const processedCount = totalCount - unprocessedCount;

  // Calculate estimated time remaining
  const getEstimatedTime = () => {
    if (!currentJob || currentJob.status !== 'processing' || currentJob.processedItems === 0) {
      return null;
    }
    
    const remainingItems = currentJob.totalItems - currentJob.processedItems;
    // With parallel processing, estimate ~3-5 seconds per item average
    const estimatedSeconds = remainingItems * 4; 
    
    if (estimatedSeconds < 60) {
      return `~${Math.round(estimatedSeconds)}s`;
    } else {
      return `~${Math.round(estimatedSeconds / 60)}m`;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          ⚡ High-Speed Enrichment Queue
          {currentJob && (
            <Badge className={getStatusColor(currentJob.status)}>
              {getStatusIcon(currentJob.status)}
              {currentJob.status.toUpperCase()}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* API Keys Section */}
        {!keysStored && (
          <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
            <h4 className="font-medium">Store API Keys Securely</h4>
            <p className="text-sm text-muted-foreground">
              Your API keys will be encrypted and stored securely on the server for queue processing.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="openai-key">OpenAI API Key</Label>
                <Input
                  id="openai-key"
                  type="password"
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  placeholder="sk-..."
                />
              </div>
              <div>
                <Label htmlFor="perplexity-key">Perplexity API Key (Recommended)</Label>
                <Input
                  id="perplexity-key"
                  type="password"
                  value={perplexityKey}
                  onChange={(e) => setPerplexityKey(e.target.value)}
                  placeholder="pplx-..."
                />
              </div>
            </div>
            
            <Button 
              onClick={handleStoreKeys}
              disabled={!openaiKey && !perplexityKey}
              className="w-full"
            >
              Store API Keys Securely
            </Button>
          </div>
        )}

        {/* Current Status */}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">
              {processedCount} of {totalCount} investors processed
            </p>
            {unprocessedCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {unprocessedCount} remaining to process
              </p>
            )}
          </div>
          
          {keysStored && (
            <div className="flex gap-2">
              {/* Cancel Button */}
              {currentJob && ['pending', 'processing'].includes(currentJob.status) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isCancelling}
                      className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                    >
                      {isCancelling ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <StopCircle className="h-4 w-4" />
                          Cancel Job
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-500" />
                        Cancel Enrichment Job?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will stop the enrichment process. Items currently being processed will complete, 
                        but no new items will be started. You can restart the job later to continue from where it left off.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep Running</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCancelJob}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        Yes, Cancel Job
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {/* Start Button */}
              <Button
                onClick={handleStartEnrichment}
                disabled={
                  isSubmitting || 
                  !relevanceContext || 
                  unprocessedCount === 0 || 
                  (currentJob && ['pending', 'processing'].includes(currentJob.status))
                }
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  `⚡ Process ${unprocessedCount} Investors`
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Job Progress */}
        {currentJob && (
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex justify-between items-center">
              <h4 className="font-medium flex items-center gap-2">
                Current Job Progress
                {currentJob.status === 'processing' && (
                  <Badge variant="outline" className="text-blue-600">
                    Parallel Processing
                  </Badge>
                )}
              </h4>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{Math.round(currentJob.progress)}%</span>
                {getEstimatedTime() && (
                  <span className="text-blue-600">• {getEstimatedTime()} remaining</span>
                )}
              </div>
            </div>
            
            <Progress value={currentJob.progress} className="w-full" />
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total:</span>
                <span className="ml-2 font-medium">{currentJob.totalItems}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Processed:</span>
                <span className="ml-2 font-medium text-green-600">{currentJob.processedItems}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Failed:</span>
                <span className="ml-2 font-medium text-red-600">{currentJob.failedItems}</span>
              </div>
            </div>

            {currentJob.status === 'completed' && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Enrichment job completed! {currentJob.processedItems - currentJob.failedItems} investors successfully processed.
                </AlertDescription>
              </Alert>
            )}

            {currentJob.status === 'cancelled' && (
              <Alert>
                <StopCircle className="h-4 w-4" />
                <AlertDescription>
                  Job was cancelled. {currentJob.processedItems} investors were processed before cancellation.
                  {currentJob.cancellationReason && ` Reason: ${currentJob.cancellationReason}`}
                </AlertDescription>
              </Alert>
            )}

            {currentJob.recentFailures && currentJob.recentFailures.length > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  Recent failures: {currentJob.recentFailures.slice(0, 3).map(f => f.investor_original_id).join(', ')}
                  {currentJob.recentFailures.length > 3 && ` and ${currentJob.recentFailures.length - 3} more`}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Performance Info */}
        {!currentJob && keysStored && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>⚡ Enhanced Performance:</strong> This queue now processes investors in parallel, 
              making it 10-20x faster than before. Expect 100+ investors to complete in 1-2 minutes instead of hours.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default EnrichmentQueue;
